import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import ClientRedirect from './components/ClientRedirect';
import Login from './pages/Login';
import AdminPortal from './pages/AdminPortal';
import Dashboard from './pages/Dashboard';
import SignatureTool from './pages/SignatureTool';
import CompanySettings from './pages/CompanySettings';
import LandingPage from './pages/LandingPage';
import Pricing from './pages/Pricing';
import Checkout from './pages/Checkout';
import CheckoutSuccess from './pages/CheckoutSuccess';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import EmailSettings from './pages/EmailSettings';
import StripeSettings from './pages/StripeSettings';
import TrialSettings from './pages/TrialSettings';
import VerifyEmail from './pages/VerifyEmail';
import EmailTemplates from './pages/EmailTemplates';
import BlogManagement from './pages/BlogManagement';
import BlogEditor from './pages/BlogEditor';
import BlogPost from './pages/BlogPost';
import BlogListing from './pages/BlogListing';
import TrackingCodes from './pages/TrackingCodes';
import TrackingCodeInjector from './components/TrackingCodeInjector';
import './App.css';

function App() {
  useEffect(() => {
    // Global error handler for unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      // Log with details for debugging
      if (event.reason) {
        console.warn('Caught unhandled rejection:', {
          reason: event.reason,
          message: event.reason?.message,
          stack: event.reason?.stack,
          type: typeof event.reason
        });
      } else {
        console.warn('Caught unhandled rejection with undefined reason');
      }
      
      // DO NOT block events - just log for debugging
      // Removed: event.stopImmediatePropagation();
      // Removed: event.preventDefault();
      // Removed: return false;
    };

    // Use bubble phase (false) instead of capture phase to avoid blocking all events
    window.addEventListener('unhandledrejection', handleUnhandledRejection, false);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, false);
    };
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <TrackingCodeInjector />
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          
          {/* Public Blog Routes */}
          <Route path="/blog" element={<BlogListing />} />
          <Route path="/blog/category/:categorySlug" element={<BlogListing />} />
          <Route path="/blog/:categorySlug/:slug" element={<BlogPost />} />
          <Route path="/blog/:slug" element={<BlogPost />} /> {/* Legacy support with redirect */}
          
          {/* Protected Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireSuperAdmin={true}>
                <AdminPortal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/email-settings"
            element={
              <ProtectedRoute requireSuperAdmin={true}>
                <EmailSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/stripe-settings"
            element={
              <ProtectedRoute requireSuperAdmin={true}>
                <StripeSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/trial-settings"
            element={
              <ProtectedRoute requireSuperAdmin={true}>
                <TrialSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/email-templates"
            element={
              <ProtectedRoute requireSuperAdmin={true}>
                <EmailTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tracking-codes"
            element={
              <ProtectedRoute requireSuperAdmin={true}>
                <TrackingCodes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blog"
            element={
              <ProtectedRoute requireSuperAdmin={true}>
                <BlogManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blog/new"
            element={
              <ProtectedRoute requireSuperAdmin={true}>
                <BlogEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blog/edit/:postId"
            element={
              <ProtectedRoute requireSuperAdmin={true}>
                <BlogEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute requireAdmin={true}>
                <CompanySettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Signature tool - public access, admin can login for settings */}
          <Route path="/signature/:signatureUrl" element={<SignatureTool />} />
          
          {/* Redirect old client URLs to include index.html for backward compatibility */}
          <Route path="/clients/:clientName" element={<ClientRedirect />} />
          </Routes>
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
